import { Component, Inject, Input, OnInit, Optional, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { IChoosenAudiencesInterface } from 'app/library/all-users/interfaces/choosen-audiences.interface';
import { IChoosenExperiencesInterface } from 'app/library/all-users/interfaces/choosen-experiences.interface';
import { IChoosenFilterInterface } from 'app/library/all-users/interfaces/choosen-filter.interface';
import { IChoosenModulesInterface } from 'app/library/all-users/interfaces/choosen-modules.interface';
import { IChoosenTeamsInterface } from 'app/library/all-users/interfaces/choosen-teams.interface';
import { TextFilterEntry } from 'app/library/all-users/interfaces/extended-attribute-filer.interface';
import { UserRole } from 'app/library/all-users/models/user-role';
import { AllUsersApiService } from 'app/library/all-users/services/all-users.api-service';
import { UNASSIGNED_AUDIENCE_FILTER_TYPE } from 'app/library/audience/constants/unassigned-audience-filter.constant';
import { MaxBrainAudience } from 'app/library/audience/models/audience.model';
import { UNASSIGNED_EXPERIENCE_FILTER_TYPE } from 'app/library/experience/constants/unassigned-experience-filter.constant';
import { MaxBrainExperience } from 'app/library/experience/models/experience.model';
import { ExtendedAttribute } from 'app/library/extended-attributes/models/extended-attribute';
import { ExtendedAttributeFilter } from 'app/library/extended-attributes/models/extended-attribute-filter.model';
import { ExtendedAttributesState } from 'app/library/extended-attributes/models/extended-attributes.state';
import { ExtendedAttributeApiService } from 'app/library/extended-attributes/services/extended-attribute.api-service';
import { ExtendedAttributeService } from 'app/library/extended-attributes/services/extended-attribute.service';
import { AdminPermissions } from 'app/library/feature/enums/admin-permissions.enum';
import { UNASSIGNED_MODULE_FILTER_TYPE } from 'app/library/module/constants/unassigned-module-filter.constant';
import { Module } from 'app/library/module/models/module.model';
import { MaxBrainTag } from 'app/library/tag/models/tag.model';
import { TagMultiSelectService } from 'app/library/tag/services/multi-select.service';
import { TagService } from 'app/library/tag/services/tag.service';
import { UNASSIGNED_TEAM_FILTER_TYPE } from 'app/library/team/constants/unassigned-team-filter.constant';
import { Team } from 'app/library/team/models/team.model';
import { TeamRoleFilterOptions } from 'app/library/team/models/team-role-filter-options.model';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { FeatureSwitchPipe } from 'app/projects/shared/src/lib/pipes/feature-switch.pipe';
import { User } from 'app/projects/user/src/lib/models/user';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ExtendedAttributeCategoryEnum } from '../../../../extended-attributes/enums/extended-attribute-category.enum';
import { ExtendedAttributeType } from '../../../../extended-attributes/enums/extended-attribute-type.enum';
import { AttributeWithOptionsFilterComponent } from '../../filter/attribute-with-options-filter/attribute-with-options-filter.component';
import { AudienceFilterComponent } from '../../filter/audience-filter/audience-filter.component';
import { ExperienceFilterComponent } from '../../filter/experience-filter/experience-filter.component';
import { TeamFilterComponent } from '../../filter/team-filter/team-filter.component';
import { BuiltInUserTextFieldKey } from '../../filter/text-filter/built-in-user-text-fields';
import { SelectedFilterChipVm, SelectedFilterGroupVm } from '../../selected-filters-chip/selected-filter-chip.vm';
import { FilterByModuleComponent } from '../filter-by-module/filter-by-module-dialog.component';

export interface FilterByDialogConfig {
    isCommunityPage?: boolean;
    excludeAudienceFilter?: boolean;
    filtersListStorageKey?: string;
    saveMode?: 'allUsers' | 'automaticAudience';
    extendedAttributeCategory?: ExtendedAttributeCategoryEnum;
    isUnassignedModuleSelected?: boolean;
    isUnassignedExperienceSelected?: boolean;
    isUnassignedTeamSelected?: boolean;
    isUnassignedAudienceSelected?: boolean;
}

/**
 * Multi-category "Filter" dialog (sidebar categories + options + always-visible active filters).
 *
 * Used by:
 * - All Users (`main/pages/all-users/user-list`)
 * - Community users (`main/pages/community`)
 * - Automatic audiences (`library/grow/.../audience-users`)
 *
 * Same UI pattern also applied in:
 * - `CourseFilterByDialogComponent` — all executions, offerings search, structure category,
 *   offering-details executions, offering-details listings
 * - `BookingsFilterByDialogComponent` — participants tab, participants dialog
 */
@Component({
    selector: 'app-filter-by-dialog',
    templateUrl: './filter-by-dialog.component.html',
    styleUrls: ['./filter-by-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'filter-by-dialog-host',
    },
})
export class FilterByDialogComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    isCommunityPage: boolean = false;

    @Input()
    excludeAudienceFilter = false;

    @Input()
    filtersListStorageKey: string = null;

    @Input()
    saveMode: 'allUsers' | 'automaticAudience' = 'allUsers';

    @Input()
    extendedAttributeCategory: ExtendedAttributeCategoryEnum;

    profile: User;
    list: IChoosenFilterInterface[] = [];
    removable = true;
    /** Updated only when `list` changes — avoid getters in the template (they run every change detection). */
    selectedFiltersTotal = 0;
    selectedFiltersGroups: SelectedFilterGroupVm[] = [];
    filterCategoryCounts: Record<string, number> = {};

    filters: {
        name: string;
        translationKey: string;
        isUserAttribute: boolean;
        attributeName?: string;
        builtInUserTextField?: BuiltInUserTextFieldKey;
    }[] = [];

    private readonly _entityTypesByFilterName: Record<string, IChoosenFilterInterface['entity']['type'][]> = {
        module: ['Module', 'UnassignedModule'],
        userRole: ['UserRole'],
        status: ['Status'],
        audience: ['Audience', 'UnassignedAudience'],
        experience: ['Experience', 'UnassignedExperience'],
        team: ['Team', 'TeamRole', 'UnassignedTeam'],
        CreatedOn: ['CreatedOn'],
        LastModified: ['LastModified'],
    };

    userAttributes: ExtendedAttribute[];
    filterValues: ExtendedAttributeFilter[];
    selectedUserAttributeFilterValue: ExtendedAttributeFilter;
    selectedUserAttributeFilter: ExtendedAttribute;
    selectedBuiltInUserTextField: BuiltInUserTextFieldKey | null = null;
    selectedUserAttributeFilter$: Observable<ExtendedAttribute>;
    ExtendedAttributeType = ExtendedAttributeType;
    isExtendedAttributesFeatureEnabled: boolean;
    isEngageFeatureEnabled: boolean;
    isTeamsFeatureEnabled: boolean;

    selectedFilter = new FormControl();

    selectedExperiences: MaxBrainExperience[];
    selectedAudiences: MaxBrainAudience[];
    selectedTeams: Team[];
    selectedModules: Module[];
    selectedRoles: UserRole[];
    isSelectedDateRangeFilter = false;
    isUnassignedExperienceSelected: boolean;
    isUnassignedAudienceSelected: boolean;
    isUnassignedTeamSelected: boolean;
    isUnassignedModuleSelected: boolean;
    isLoading: boolean;

    @ViewChild(FilterByModuleComponent) moduleList: FilterByModuleComponent;
    @ViewChild(ExperienceFilterComponent) experienceList: ExperienceFilterComponent;
    @ViewChild(AudienceFilterComponent) audienceList: AudienceFilterComponent;
    @ViewChild(TeamFilterComponent) teamList: TeamFilterComponent;
    @ViewChild(AttributeWithOptionsFilterComponent) attributeOptionsList: AttributeWithOptionsFilterComponent;

    constructor(
        public dialogRef: MatDialogRef<FilterByDialogComponent>,
        public tagMultiSelectService: TagMultiSelectService,
        public tagService: TagService,
        private _extendedAttributeService: ExtendedAttributeService,
        private _extendedAttributeApiService: ExtendedAttributeApiService,
        private _allUsersService: AllUsersApiService,
        private _featureSwitchPipe: FeatureSwitchPipe,
        private _store: Store,
        private _translate: TranslateService,
        @Optional() @Inject(MAT_DIALOG_DATA) config: FilterByDialogConfig | null
    ) {
        super();

        if (config) {
            Object.assign(this, config);
        }

        this.isExtendedAttributesFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.ExtendedAttributes);
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;
        if (!this.isCommunityPage) {
            this.filters.push(
                { name: 'module', translationKey: 'MODULE.SINGULAR', isUserAttribute: false },
                { name: 'userRole', translationKey: 'GENERAL.LABEL.USER_ROLE', isUserAttribute: false }
            );

            if (this.saveMode === 'automaticAudience') {
                this.filters.push({ name: 'status', translationKey: 'GENERAL.PROPERTY.STATUS', isUserAttribute: false });
            }

            this.isEngageFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.Engage);
            if (this.isEngageFeatureEnabled) {
                if (!this.excludeAudienceFilter) {
                    this.filters.push(
                        { name: 'audience', translationKey: 'ENGAGE.AUDIENCE.TITLE.SINGULAR', isUserAttribute: false }
                    );
                }

                this.filters.push({ name: 'experience', translationKey: 'ENGAGE.EXPERIENCE.TITLE.SINGULAR', isUserAttribute: false });
            }

            this.isTeamsFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.Teams);
            if (this.isTeamsFeatureEnabled) {
                this.filters.push({ name: 'team', translationKey: 'TEAMS.TITLE.SINGULAR', isUserAttribute: false });
            }
        }

        this.filters.push(
            { name: 'CreatedOn', translationKey: 'GENERAL.PROPERTY.CREATED_ON', isUserAttribute: false, attributeName: 'createdAt' },
            { name: 'LastModified', translationKey: 'GENERAL.PROPERTY.LAST_MODIFIED', isUserAttribute: false, attributeName: 'updatedAt' }
        );

        if (this.isExtendedAttributesFeatureEnabled) {
            await this._extendedAttributeService.fetchExtendedAttributes([this.extendedAttributeCategory]);
            this.userAttributes = this._store.selectSnapshot(ExtendedAttributesState.getFilteredEntities);

            this.profile = this._store.selectSnapshot(UserState.getMyInfo);

            if (this.profile.permissions.includes(AdminPermissions.TENANT_MANAGEMENT)) {
                this.filterValues = await this._extendedAttributeApiService.fetchFilterValues();
            } else {
                this.filterValues = await this._extendedAttributeApiService.fetchParticipantFilterValues();
            }

            this.selectedUserAttributeFilter = this.userAttributes[0];
            this.selectedUserAttributeFilterValue = this.filterValues.find((value) => value.extendedAttributeId === this.selectedUserAttributeFilter?.id);

            const builtInUserTextFilters = [
                {
                    name: '__builtinText_firstName',
                    translationKey: 'GENERAL.PROPERTY.FIRST_NAME',
                    isUserAttribute: true,
                    builtInUserTextField: 'firstName' as const,
                },
                {
                    name: '__builtinText_lastName',
                    translationKey: 'GENERAL.PROPERTY.LAST_NAME',
                    isUserAttribute: true,
                    builtInUserTextField: 'lastName' as const,
                },
                {
                    name: '__builtinText_email',
                    translationKey: 'GENERAL.PROPERTY.EMAIL',
                    isUserAttribute: true,
                    builtInUserTextField: 'email' as const,
                },
            ];

            const extendedAttributeFilters = this.userAttributes.map((attribute) => {
                return {
                    name: attribute.name,
                    translationKey: attribute.translationKey,
                    isUserAttribute: true,
                };
            });

            this.filters = [...this.filters, ...builtInUserTextFilters, ...extendedAttributeFilters];
        }

        this._allUsersService.filtersList$.pipe(takeUntil(this._unsubscribeAll)).subscribe((filters) => {
            this.list = filters;
            this._refreshSelectedFiltersPanel();
        });

        if (this.saveMode === 'automaticAudience') {
            this._allUsersService.setFiltersFromStorage(this._getFiltersListStorageKey());
        }

        this.selectedFilter.setValue(this.filters[0]);
        const initialFilter = this.selectedFilter.value;
        if (initialFilter) {
            this.filterChanged(initialFilter);
        }

        this.isLoading = false;
    }

    trackFilterCategory(_: number, item: { name: string }): string {
        return item.name;
    }

    trackSelectedFilterGroup(_: number, group: SelectedFilterGroupVm): string {
        return group.groupKey;
    }

    trackSelectedFilterChip(_: number, chip: SelectedFilterChipVm): string {
        return `${chip.entityType}:${chip.entityId}:${chip.attributeId ?? ''}:${chip.textFilterIndex ?? ''}:${chip.chipLabel}`;
    }

    selectFilterCategory(filter: {
        name: string;
        translationKey: string;
        isUserAttribute: boolean;
        attributeName?: string;
        builtInUserTextField?: BuiltInUserTextFieldKey;
    }): void {
        this.selectedFilter.setValue(filter);
        this.filterChanged(filter);
    }

    private _getFiltersListStorageKey(): string {
        if (this.filtersListStorageKey) {
            return this.filtersListStorageKey;
        }

        return this.isCommunityPage ? 'communityUsersFiltersList' : 'allUsersFiltersList';
    }

    removeFromList(entity: IChoosenFilterInterface, index: number): void {
        if (entity && index > -1) {
            this._allUsersService.setFiltersList(this._getFiltersListStorageKey(), [...this.list.filter((item, idx) => idx !== index)]);
        }
    }

    removeFilterChip(chip: SelectedFilterChipVm): void {
        const listKey = this._getFiltersListStorageKey();
        const listIndex = this.list.findIndex(
            (item) => item.entity.id === chip.entityId && item.entity.type === chip.entityType && (chip.attributeId == null || item.attributeId === chip.attributeId)
        );

        if (listIndex < 0) {
            return;
        }

        const item = this.list[listIndex];

        if (chip.textFilterIndex != null && item.textFilters?.length) {
            const nextTextFilters = item.textFilters.filter((_, i) => i !== chip.textFilterIndex);
            if (nextTextFilters.length === 0) {
                this._allUsersService.setFiltersList(
                    listKey,
                    this.list.filter((_, i) => i !== listIndex)
                );
            } else {
                const next = [...this.list];
                next[listIndex] = {
                    ...item,
                    textFilters: nextTextFilters,
                    valueToShow: this._rebuildTextFilterValueToShow(item, nextTextFilters),
                };
                this._allUsersService.setFiltersList(listKey, next);
            }
        } else {
            this._allUsersService.setFiltersList(
                listKey,
                this.list.filter((_, i) => i !== listIndex)
            );

            if (chip.entityType === UNASSIGNED_AUDIENCE_FILTER_TYPE) {
                this.isUnassignedAudienceSelected = false;
            }

            if (chip.entityType === UNASSIGNED_EXPERIENCE_FILTER_TYPE) {
                this.isUnassignedExperienceSelected = false;
            }

            if (chip.entityType === UNASSIGNED_MODULE_FILTER_TYPE) {
                this.isUnassignedModuleSelected = false;
            }

            if (chip.entityType === UNASSIGNED_TEAM_FILTER_TYPE) {
                this.isUnassignedTeamSelected = false;
            }
        }
    }

    private _refreshSelectedFiltersPanel(): void {
        this.selectedFiltersGroups = this._buildGroupedSelectedFilters(this.list);
        this.selectedFiltersTotal = this.selectedFiltersGroups.reduce((sum, g) => sum + g.chips.length, 0);
        this.filterCategoryCounts = this._buildFilterCategoryCounts(this.list);
    }

    private _buildFilterCategoryCounts(list: IChoosenFilterInterface[]): Record<string, number> {
        const counts: Record<string, number> = {};

        for (const filter of this.filters) {
            const matching = list.filter((item) => this._itemMatchesFilterCategory(item, filter));
            counts[filter.name] = matching.reduce((sum, item) => sum + (item.textFilters?.length || 1), 0);
        }

        return counts;
    }

    private _itemMatchesFilterCategory(
        item: IChoosenFilterInterface,
        filter: {
            name: string;
            isUserAttribute: boolean;
            builtInUserTextField?: BuiltInUserTextFieldKey;
        }
    ): boolean {
        if (filter.builtInUserTextField) {
            return item.entity.type === 'UserProfileTextFilter' && item.entity.id === filter.builtInUserTextField;
        }

        if (filter.isUserAttribute) {
            const attribute = this.userAttributes?.find((attr) => attr.name === filter.name);
            if (!attribute) {
                return false;
            }

            return item.attributeId === attribute.id || item.entity.id === attribute.id;
        }

        const entityTypes = this._entityTypesByFilterName[filter.name];
        return !!entityTypes?.includes(item.entity.type);
    }

    private _buildGroupedSelectedFilters(list: IChoosenFilterInterface[]): SelectedFilterGroupVm[] {
        const order: string[] = [];
        const map = new Map<string, { categoryLabel: string; chips: SelectedFilterChipVm[] }>();

        for (const item of list) {
            const groupKey = this._selectedFilterGroupKey(item);
            const { category } = this._parseValueToShowPrefix(item.valueToShow);

            if (!map.has(groupKey)) {
                map.set(groupKey, { categoryLabel: category, chips: [] });
                order.push(groupKey);
            }

            const group = map.get(groupKey)!;

            if (item.textFilters?.length) {
                item.textFilters.forEach((tf, tfIndex) => {
                    group.chips.push({
                        chipLabel: `${tf.value} (${this._textFilterModeLabel(tf.mode)})`,
                        entityId: item.entity.id,
                        entityType: item.entity.type,
                        attributeId: item.attributeId,
                        textFilterIndex: tfIndex,
                    });
                });
            } else {
                group.chips.push({
                    chipLabel: this._singleChipLabel(item),
                    entityId: item.entity.id,
                    entityType: item.entity.type,
                    attributeId: item.attributeId,
                });
            }
        }

        const palette = MaxBrainTag.colorsArray;
        return order.map((key, idx) => ({
            groupKey: key,
            categoryLabel: map.get(key)!.categoryLabel,
            color: palette[idx % palette.length],
            chips: map.get(key)!.chips,
        }));
    }

    private _selectedFilterGroupKey(item: IChoosenFilterInterface): string {
        if (item.attributeId) {
            return `attr:${item.attributeId}`;
        }
        if (item.entity.type === 'UnassignedTeam') {
            return 'type:Team';
        }
        if (item.entity.type === 'UnassignedAudience') {
            return 'type:Audience';
        }
        if (item.entity.type === 'UnassignedExperience') {
            return 'type:Experience';
        }
        if (item.entity.type === 'UnassignedModule') {
            return 'type:Module';
        }
        return `type:${item.entity.type}`;
    }

    private _parseValueToShowPrefix(valueToShow: string): { category: string; rest: string } {
        const idx = valueToShow.indexOf(': ');
        if (idx === -1) {
            return { category: valueToShow, rest: '' };
        }
        return { category: valueToShow.slice(0, idx), rest: valueToShow.slice(idx + 2) };
    }

    private _singleChipLabel(item: IChoosenFilterInterface): string {
        const { rest } = this._parseValueToShowPrefix(item.valueToShow);
        return rest || item.valueToShow;
    }

    private _textFilterModeLabel(mode: 'like' | 'eq'): string {
        return mode === 'like' ? this._translate.instant('GENERAL.LABEL.CONTAINS') : this._translate.instant('GENERAL.LABEL.IS_EXACTLY');
    }

    private _rebuildTextFilterValueToShow(item: IChoosenFilterInterface, textFilters: TextFilterEntry[]): string {
        const { category } = this._parseValueToShowPrefix(item.valueToShow);
        const body = textFilters.map((tf) => `${tf.value} (${this._textFilterModeLabel(tf.mode)})`).join(', ');
        return `${category}: ${body}`;
    }

    addToList(entities: IChoosenFilterInterface[]): void {
        this._allUsersService.setFiltersList(this._getFiltersListStorageKey(), entities, false);
    }

    save(): void {
        if (this.saveMode === 'automaticAudience') {
            this._allUsersService.setFiltersList(this._getFiltersListStorageKey(), this.list, true);
            this.dialogRef.close({
                saveMode: 'automaticAudience',
                filtersList: this.list,
                isUnassignedModuleSelected: this.list.some((entity) => entity.entity.type === UNASSIGNED_MODULE_FILTER_TYPE),
                isUnassignedExperienceSelected: this.list.some((entity) => entity.entity.type === UNASSIGNED_EXPERIENCE_FILTER_TYPE),
                isUnassignedTeamSelected: this.list.some((entity) => entity.entity.type === UNASSIGNED_TEAM_FILTER_TYPE),
            });
            return;
        }

        this.dialogRef.close({
            data: {
                experience: {
                    selectedExperiences: this.list.some((entity) => entity.entity.type === UNASSIGNED_EXPERIENCE_FILTER_TYPE)
                        ? []
                        : this.list.filter((entity) => entity.entity.type === 'Experience').map((ent) => ent.entity),
                    isUnassignedSelected: this.list.some((entity) => entity.entity.type === UNASSIGNED_EXPERIENCE_FILTER_TYPE),
                },
                audience: {
                    selectedAudiences: this.list.some((entity) => entity.entity.type === UNASSIGNED_AUDIENCE_FILTER_TYPE)
                        ? []
                        : this.list.filter((entity) => entity.entity.type === 'Audience').map((ent) => ent.entity),
                    isUnassignedSelected: this.list.some((entity) => entity.entity.type === UNASSIGNED_AUDIENCE_FILTER_TYPE),
                },
                team: {
                    selectedTeams: this.list.some((entity) => entity.entity.type === UNASSIGNED_TEAM_FILTER_TYPE)
                        ? []
                        : this.list.filter((entity) => entity.entity.type === 'Team').map((ent) => ent.entity),
                    isUnassignedSelected: this.list.some((entity) => entity.entity.type === UNASSIGNED_TEAM_FILTER_TYPE),
                    teamRoleIds: this.list.some((entity) => entity.entity.type === UNASSIGNED_TEAM_FILTER_TYPE) ? [] : this._getTeamRoleIdsFromList(),
                },
                module: {
                    selectedModules: this.list.some((entity) => entity.entity.type === UNASSIGNED_MODULE_FILTER_TYPE)
                        ? []
                        : this.list.filter((entity) => entity.entity.type === 'Module').map((ent) => ent.entity),
                    isUnassignedSelected: this.list.some((entity) => entity.entity.type === UNASSIGNED_MODULE_FILTER_TYPE),
                },                userRole: this.list.filter((entity) => entity.entity.type === 'UserRole').map((ent) => ent.entity),
                status: this.list.filter((entity) => entity.entity.type === 'Status').map((ent) => ent.entity),
                attributes: {
                    data: this.list.filter(
                        (attr) =>
                            attr.entity.type === 'ExtendedAttribute' ||
                            attr.entity.type === 'ExtendedAttributeOption' ||
                            attr.entity.type === 'UserProfileTextFilter' ||
                            attr.entity.type === 'CreatedOn' ||
                            attr.entity.type === 'LastModified'
                    ),
                },
            },
        });

        this._allUsersService.setFiltersList(this._getFiltersListStorageKey(), this.list, true);
    }

    clearFilters(): void {
        // Draft only — persist on Save; Cancel restores from session storage.
        this.isUnassignedExperienceSelected = false;
        this.isUnassignedAudienceSelected = false;
        this.isUnassignedTeamSelected = false;
        this.isUnassignedModuleSelected = false;
        this._allUsersService.setFiltersList(this._getFiltersListStorageKey(), [], false);
    }

    cancel(): void {
        // Always restore the last saved filters (ignore in-dialog draft).
        this._allUsersService.setFiltersFromStorage(this._getFiltersListStorageKey());
        this.dialogRef.close(false);
    }

    filterChanged(event: {
        name: string;
        translationKey: string;
        isUserAttribute: boolean;
        attributeName?: string;
        builtInUserTextField?: BuiltInUserTextFieldKey;
    }): void {
        this.isSelectedDateRangeFilter = event.name === 'CreatedOn' || event.name === 'LastModified';

        if (!event.isUserAttribute) {
            this.selectedBuiltInUserTextField = null;
            this.selectedUserAttributeFilter = null;
            this.selectedUserAttributeFilterValue = null;
            return;
        }

        if (event.builtInUserTextField) {
            this.selectedBuiltInUserTextField = event.builtInUserTextField;
            this.selectedUserAttributeFilter = null;
            this.selectedUserAttributeFilterValue = null;
            return;
        }

        this.selectedBuiltInUserTextField = null;
        this.selectedUserAttributeFilter = this.userAttributes?.find((attr) => attr.name === event.name);
        this.selectedUserAttributeFilterValue = this.filterValues?.find((value) => value.extendedAttributeId === this.selectedUserAttributeFilter?.id);
    }

    loadExperienceList(payload: IChoosenExperiencesInterface): void {
        this.selectedExperiences = payload.experiences;
        this.isUnassignedExperienceSelected = payload.isUnassignedSelected;
    }

    loadAudienceList(payload: IChoosenAudiencesInterface): void {
        this.selectedAudiences = payload.audiences as MaxBrainAudience[];
        this.isUnassignedAudienceSelected = payload.isUnassignedSelected;
    }

    loadTeamList(payload: IChoosenTeamsInterface): void {
        this.selectedTeams = payload.teams as Team[];
        this.isUnassignedTeamSelected = payload.isUnassignedSelected;
    }

    loadModuleList(payload: IChoosenModulesInterface): void {
        this.selectedModules = payload.modules;
        this.isUnassignedModuleSelected = payload.isUnassignedSelected;
    }

    loadUserRoleList(roles: UserRole[]): void {
        this.selectedRoles = roles;
    }

    private _getTeamRoleIdsFromList(): string[] {
        const teamRoleFilters = this.list.filter((entity) => entity.entity.type === 'TeamRole');

        if (!teamRoleFilters.length) {
            return TeamRoleFilterOptions.defaultRoleIds;
        }

        return teamRoleFilters.map((entity) => entity.entity.id);
    }
}
